import axios from "axios";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { SparklesCore } from "../../../../../shared/animations/sparkles/soarklesCore";
import { API_ENDPOINT } from "../../../../../../lib/api-url";
import { useWebSocket } from "../../../../../../contexts/WebSocketContext";

// 1) A memoized wrapper so SparklesCore never re-renders
const SparklesOverlay = React.memo(function SparklesOverlay() {
  return (
    <SparklesCore
      id="tsparticlesfullpage"
      background="transparent"
      minSize={0.6}
      maxSize={1.4}
      particleDensity={30}
      className="w-full h-full absolute top-[0] left-[50%] translate-x-[-50%] overflow-hidden"
      particleColor="#E8232C"
    />
  );
});

// shallow-ish compare for leaderboard rows
function sameUsers(a, b) {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i],
      y = b[i];
    if (
      x.id !== y.id ||
      x.name !== y.name ||
      x.points !== y.points ||
      x.correct !== y.correct ||
      x.wrong !== y.wrong ||
      x.rank !== y.rank
    )
      return false;
  }
  return true;
}

export function QuestionsResultTableComponent() {
  const [users, setUsers] = useState([]);
  const { socketData } = useWebSocket();

  const challengeId = socketData?.data?.challenge_id;

  const lastUsersRef = useRef(users);

  const fetchResults = async (cid) => {
    if (!cid) return;
    try {
      const result = await axios.get(
        `${API_ENDPOINT.wallQuestions.getUserBoard}/${cid}`
      );
      if (result.data?.data) {
        const mapped = result.data.data.map((u, index) => {
          const correct = u.results.filter((r) => r.result === true).length;
          const wrong = u.results.filter((r) => r.result === false).length;
          return {
            id: u.user_id,
            name: `${u.first_name} ${u.last_name}`,
            points: u.points,
            correct,
            wrong,
            rank: index + 1,
          };
        });

        // Only update if something actually changed
        if (!sameUsers(lastUsersRef.current, mapped)) {
          lastUsersRef.current = mapped;
          setUsers(mapped);
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  // Initial load
  useEffect(() => {
    fetchResults(challengeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // mount only

  // Refetch ONLY when the challenge_id changes (not every socket tick)
  useEffect(() => {
    fetchResults(challengeId);
  }, [challengeId]);

  // split two columns
  const firstColumn = users.slice(0, 10);
  const secondColumn = users.slice(10, 20);

  // Small row component, memoized so React skips re-render if props identical
  const UserRow = React.memo(function UserRow({ user, withTrophy }) {
    return (
      <div className="relative" key={user.id}>
        <img
          className="w-[436px]"
          src="/container/player-box-container.png"
          alt=""
        />
        <img
          src="/avatar/avatar-default.webp"
          className="w-[43px] absolute right-[20px] bottom-[7px] z-10"
          alt=""
        />

        {withTrophy && user.rank <= 4 && (
          <div className="absolute top-[-2px] left-0  flex z-[1] items-center justify-center">
            <svg
              width="19"
              height="23"
              viewBox="0 0 19 23"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18.6328 22.5374L5.94045 0.647461H0.744873L13.282 22.5374H18.6328Z"
                fill="#EEFF00"
              />
            </svg>
          </div>
        )}

        <div className="absolute top-0 left-0 w-full h-full flex z-[2] items-center justify-start gap-[24px] flex-row-reverse">
          <span className="text-[#FFFFFF] text-[8px] font-bold mr-[74px] w-[120px] truncate">
            {user.name}
          </span>
          <div className="w-full flex justify-end items-center gap-[44px]">
            <span className="text-[#FFFFFF] text-[10px] font-bold">
              {user.points}
            </span>
            <span className="text-[#FF3A06] text-[10px] font-bold">
              {user.wrong}
            </span>
            <span className="text-[#40BF6A] text-[10px] font-bold">
              {user.correct}
            </span>
            <span className="text-[#FFFFFF] text-[10px] font-bold">
              {user.rank}
            </span>
          </div>
        </div>
      </div>
    );
  });

  // Column is also memoized; it only re-renders if its list identity changes
  const Column = React.memo(function Column({ column, withTrophy = false }) {
    return (
      <div className="list-scroll p-8 flex flex-col items-center justify-center gap-6 max-h-[600px] relative">
        <div className="table-header-container absolute top-[-35px] left-[40px] w-[281px] flex justify-center items-center gap-[33px]">
          <img
            src="/container/table-header-container.svg"
            className="w-[281px] absolute z-[0]"
            alt=""
          />
          <div className="text-container flex justify-center items-center gap-[22px] relative z-10">
            <span className="text-[#FFFFFF] text-[10px] font-bold">امتیاز</span>
            <span className="text-[#FFFFFF] text-[10px] font-bold">
              پاسخ غلط
            </span>
            <span className="text-[#FFFFFF] text-[10px] font-bold">
              پاسخ صحیح
            </span>
            <span className="text-[#FFFFFF] text-[10px] font-bold">رتبه</span>
          </div>
        </div>

        {column.map((user) => (
          <UserRow key={user.id} user={user} withTrophy={withTrophy} />
        ))}
      </div>
    );
  });

  // Memoize the arrays we pass to Column so identity only changes when content changes
  const firstColumnMemo = useMemo(() => firstColumn, [firstColumn]);
  const secondColumnMemo = useMemo(() => secondColumn, [secondColumn]);

  return (
    <div className="w-full flex flex-col items-center justify-center">
      {/* Mount once and never re-render */}
      <SparklesOverlay />

      <div>
        <h2 className="text-[#FFFFFF] font-bold text-[26px] flex items-center justify-center">
          رده بندی نهایی پرسش و پاسخ
        </h2>
        <div className="relative mt-2 h-20 w-[40rem]">
          <div className="absolute inset-x-20 top-0 h-[3px] w-3/4 bg-gradient-to-r from-transparent via-white/70 to-transparent blur-md" />
          <div className="absolute inset-x-20 top-0 h-px w-3/4 bg-gradient-to-r from-transparent via-white to-transparent drop-shadow-[0_0_8px_#ffffff]" />
          <div className="absolute inset-x-60 top-0 h-[6px] w-1/4 bg-gradient-to-r from-transparent via-white/80 to-transparent blur-md" />
          <div className="absolute inset-x-60 top-0 h-px w-1/4 bg-gradient-to-r from-transparent via-white to-transparent drop-shadow-[0_0_10px_#ffffff]" />
          <div className="absolute inset-x-44 top-0 h-[2px] w-2/4 bg-gradient-to-r from-transparent via-sky-100/60 to-transparent blur-[2px]" />
          <div className="pointer-events-none absolute inset-0 h-full w-full [mask-image:radial-gradient(350px_200px_at_top,transparent_20%,white)]" />
        </div>
      </div>

      <div className="tables-container w-full flex items-center justify-center">
        <div>
          <div className="flex flex-row-reverse gap-[150px] w-[1085px] h-[703px]">
            <Column column={firstColumnMemo} withTrophy />
            <Column column={secondColumnMemo} />
          </div>
        </div>
      </div>
    </div>
  );
}
